import React, { useState, useMemo } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  Squares2X2Icon,
  TableCellsIcon,
  CalendarIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  TruckIcon,
  CurrencyDollarIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { usePurchases } from '../hooks/usePurchases';
import {
  Purchase,
  PurchaseStatus,
  PaymentMethod,
  formatCurrency,
  getPurchaseStatusColor,
  getPaymentMethodDisplay,
} from '../api/purchases';
import { safeNumber } from '../utils/formatters';
import CreatePurchaseModal from '../components/purchases/CreatePurchaseModal';
import EditPurchaseModal from '../components/purchases/EditPurchaseModal';
import ViewPurchaseModal from '../components/purchases/ViewPurchaseModal';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';
import SortOrderButton from '../components/ui/SortOrderButton';
import AmountInput from '../components/ui/AmountInput';

interface PurchaseFilters {
  status?: PurchaseStatus;
  paymentMethod?: PaymentMethod;
  supplier?: string;
  dateRange?: {
    start: string;
    end: string;
  };
  amountRange?: {
    min: number;
    max: number;
  };
}

const Purchases: React.FC = () => {
  const {
    purchases,
    analytics,
    loading,
    error,
    createPurchase,
    updatePurchase,
    deletePurchase,
    refreshData,
  } = usePurchases();

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<PurchaseFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState<keyof Purchase>('purchaseDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(null);

  // Filter and sort purchases
  const filteredPurchases = useMemo(() => {
    return purchases.filter(purchase => {
      const matchesSearch = searchTerm === '' || 
        purchase.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        purchase.supplierName.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = !filters.status || purchase.status === filters.status;
      const matchesPaymentMethod = !filters.paymentMethod || purchase.paymentMethod === filters.paymentMethod;
      const matchesSupplier = !filters.supplier || purchase.supplierName.toLowerCase().includes(filters.supplier.toLowerCase());

      const matchesDateRange = !filters.dateRange || (
        new Date(purchase.purchaseDate) >= new Date(filters.dateRange.start) &&
        new Date(purchase.purchaseDate) <= new Date(filters.dateRange.end)
      );

      const matchesAmountRange = !filters.amountRange || (
        purchase.total >= filters.amountRange.min &&
        purchase.total <= filters.amountRange.max
      );

      return matchesSearch && matchesStatus && matchesPaymentMethod && matchesSupplier && matchesDateRange && matchesAmountRange;
    });
  }, [purchases, searchTerm, filters]);

  // Sort purchases
  const sortedPurchases = useMemo(() => {
    return [...filteredPurchases].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];

      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return sortOrder === 'asc' ? 1 : -1;
      if (bVal === undefined) return sortOrder === 'asc' ? -1 : 1;

      if (sortBy === 'purchaseDate' || sortBy === 'createdAt' || sortBy === 'updatedAt') {
        const aTime = new Date(String(aVal)).getTime();
        const bTime = new Date(String(bVal)).getTime();
        if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
          return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
        }
      }

      if (sortBy === 'total') {
        const diff = Number(aVal) - Number(bVal);
        return sortOrder === 'asc' ? diff : -diff;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return sortOrder === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [filteredPurchases, sortBy, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(sortedPurchases.length / itemsPerPage);
  const paginatedPurchases = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedPurchases.slice(start, start + itemsPerPage);
  }, [sortedPurchases, currentPage, itemsPerPage]);

  // Handlers
  const handleCreatePurchase = async (data: any) => {
    const result = await createPurchase(data);
    if (result) {
      setShowCreateModal(false);
    }
  };

  const handleUpdatePurchase = async (data: any) => {
    if (selectedPurchase) {
      const result = await updatePurchase(selectedPurchase.id, data);
      if (result) {
        setShowEditModal(false);
        setSelectedPurchase(null);
      }
    }
  };

  const handleDeletePurchase = async () => {
    if (selectedPurchase) {
      const success = await deletePurchase(selectedPurchase.id);
      if (success) {
        setShowDeleteModal(false);
        setSelectedPurchase(null);
      }
    }
  };

  const openViewModal = (purchase: Purchase) => {
    setSelectedPurchase(purchase);
    setShowViewModal(true);
  };

  const openEditModal = (purchase: Purchase) => {
    setSelectedPurchase(purchase);
    setShowEditModal(true);
  };

  const openDeleteModal = (purchase: Purchase) => {
    setSelectedPurchase(purchase);
    setShowDeleteModal(true);
  };

  const handleSort = (field: keyof Purchase) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const renderSortIndicator = (field: keyof Purchase) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc'
      ? <ChevronUpIcon className="h-4 w-4" aria-hidden />
      : <ChevronDownIcon className="h-4 w-4" aria-hidden />;
  };

  const getStatusIcon = (status: PurchaseStatus) => {
    switch (status) {
      case PurchaseStatus.COMPLETED:
        return <CheckCircleIcon className="h-4 w-4" />;
      case PurchaseStatus.PENDING:
        return <ClockIcon className="h-4 w-4" />;
      case PurchaseStatus.CANCELLED:
        return <XCircleIcon className="h-4 w-4" />;
      case PurchaseStatus.REFUNDED:
        return <TruckIcon className="h-4 w-4" />;
      default:
        return null;
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading purchases</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                onClick={refreshData}
                className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <PageHero
        title="Purchases"
        subtitle="Track bird purchases, invoices, and supplier transactions"
        actions={
          <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            New Purchase
          </button>
        }
      />

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CurrencyDollarIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Total Spent</dt>
                    <dd className="text-lg font-medium text-slate-900">
                      {formatCurrency(safeNumber(analytics.totalPurchasesAmount))}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentDuplicateIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Total Orders</dt>
                    <dd className="text-lg font-medium text-slate-900">
                      {safeNumber(analytics.totalPurchasesCount)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Completed</dt>
                    <dd className="text-lg font-medium text-slate-900">
                      {safeNumber(analytics.purchasesCountByStatus.COMPLETED)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Pending</dt>
                    <dd className="text-lg font-medium text-slate-900">
                      {safeNumber(analytics.purchasesCountByStatus.PENDING)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex-1 min-w-0">
              <div className="flex items-center w-full rounded-md border border-gray-300 bg-white shadow-sm focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500">
                <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 min-w-0 py-2 pr-3 border-0 bg-transparent sm:text-sm focus:outline-none focus:ring-0"
                  placeholder="Search by invoice number or supplier..."
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
              </button>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md ${
                    viewMode === 'grid'
                      ? 'bg-gray-100 text-slate-900'
                      : 'text-slate-400 hover:text-slate-500'
                  }`}
                >
                  <Squares2X2Icon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 rounded-md ${
                    viewMode === 'table'
                      ? 'bg-gray-100 text-slate-900'
                      : 'text-slate-400 hover:text-slate-500'
                  }`}
                >
                  <TableCellsIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <label htmlFor="purchase-sort-by" className="text-sm text-slate-700 whitespace-nowrap">
                  Sort by:
                </label>
                <select
                  id="purchase-sort-by"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value as keyof Purchase);
                    setCurrentPage(1);
                  }}
                  className="rounded-md border-gray-300 text-sm focus:border-cyan-500 focus:ring-cyan-500"
                >
                  <option value="purchaseDate">Date</option>
                  <option value="invoiceNumber">Invoice</option>
                  <option value="supplierName">Supplier</option>
                  <option value="total">Amount</option>
                  <option value="status">Status</option>
                </select>
                <SortOrderButton
                  order={sortOrder}
                  onToggle={() => {
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-slate-700">
                  Status
                </label>
                <select
                  id="status"
                  value={filters.status || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as PurchaseStatus || undefined }))}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="empty-option-1753461297392" value="">All Statuses</option>
                  {Object.values(PurchaseStatus).map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="paymentMethod" className="block text-sm font-medium text-slate-700">
                  Payment Method
                </label>
                <select
                  id="paymentMethod"
                  value={filters.paymentMethod || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod || undefined }))}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="empty-option-1753461297392" value="">All Methods</option>
                  {Object.values(PaymentMethod).map((method) => (
                    <option key={method} value={method}>
                      {getPaymentMethodDisplay(method)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="dateRange" className="block text-sm font-medium text-slate-700">
                  Date Range
                </label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={filters.dateRange?.start || ''}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      dateRange: { ...prev.dateRange, start: e.target.value }
                    }))}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                  />
                  <input
                    type="date"
                    value={filters.dateRange?.end || ''}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      dateRange: { ...prev.dateRange, end: e.target.value }
                    }))}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="amountRange" className="block text-sm font-medium text-slate-700">
                  Amount Range
                </label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <AmountInput
                    placeholder="Min"
                    value={filters.amountRange?.min ?? ''}
                    onChange={(v) => setFilters(prev => ({
                      ...prev,
                      amountRange: { min: v ?? 0, max: prev.amountRange?.max ?? 0 }
                    }))}
                    allowEmpty
                    min={0}
                    inputClassName="block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                  />
                  <AmountInput
                    placeholder="Max"
                    value={filters.amountRange?.max ?? ''}
                    onChange={(v) => setFilters(prev => ({
                      ...prev,
                      amountRange: { min: prev.amountRange?.min ?? 0, max: v ?? 0 }
                    }))}
                    allowEmpty
                    min={0}
                    inputClassName="block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Purchase List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-slate-900">
              Purchases ({sortedPurchases.length})
            </h3>
          </div>

          {paginatedPurchases.length === 0 ? (
            <EmptyState
              className="mt-0 rounded-none border-0 shadow-none bg-transparent"
              title="No purchases found"
              description={
                purchases.length === 0
                  ? 'Get started by creating your first purchase.'
                  : 'Try adjusting your search criteria.'
              }
              icon={<ShoppingCartIcon className="h-6 w-6" />}
              action={
                purchases.length === 0 ? (
                  <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary text-sm focus-pop">
                    <PlusIcon className="h-4 w-4" />
                    New Purchase
                  </button>
                ) : undefined
              }
            />
          ) : viewMode === 'grid' ? (
            // Grid View
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedPurchases.map((purchase) => (
                  <div
                    key={purchase.id}
                    className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => openViewModal(purchase)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-cyan-600">
                            {purchase.invoiceNumber}
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPurchaseStatusColor(purchase.status)}`}>
                            {getStatusIcon(purchase.status)}
                            <span className="ml-1">{purchase.status}</span>
                          </span>
                        </div>
                        
                        <h3 className="text-lg font-medium text-slate-900 truncate">
                          {purchase.supplierName}
                        </h3>
                        
                        <p className="text-sm text-slate-500 mb-2">
                          {format(new Date(purchase.purchaseDate), 'MMM dd, yyyy')}
                        </p>
                        
                        <div className="text-xl font-bold text-slate-900 mb-2">
                          {formatCurrency(purchase.total)}
                        </div>
                        
                        <p className="text-sm text-slate-500">
                          {getPaymentMethodDisplay(purchase.paymentMethod)}
                        </p>
                        
                        <p className="text-sm text-slate-500 mt-1">
                          {purchase.items.length} item{purchase.items.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Table View
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-slate-50/80">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => handleSort('invoiceNumber')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Invoice
                        {renderSortIndicator('invoiceNumber')}
                      </span>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => handleSort('supplierName')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Supplier
                        {renderSortIndicator('supplierName')}
                      </span>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => handleSort('purchaseDate')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Date
                        {renderSortIndicator('purchaseDate')}
                      </span>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => handleSort('total')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Amount
                        {renderSortIndicator('total')}
                      </span>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => handleSort('status')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Status
                        {renderSortIndicator('status')}
                      </span>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Payment
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedPurchases.map((purchase) => (
                    <tr
                      key={purchase.id}
                      className="hover:bg-slate-50/80 cursor-pointer"
                      onClick={() => openViewModal(purchase)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-cyan-600">
                        {purchase.invoiceNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {purchase.supplierName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {format(new Date(purchase.purchaseDate), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                        {formatCurrency(purchase.total)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPurchaseStatusColor(purchase.status)}`}>
                          {purchase.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {getPaymentMethodDisplay(purchase.paymentMethod)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-slate-700">
                    Showing{' '}
                    <span className="font-medium">
                      {(currentPage - 1) * itemsPerPage + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, sortedPurchases.length)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">{sortedPurchases.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-cyan-50 border-cyan-500 text-cyan-600'
                            : 'bg-white border-gray-300 text-slate-500 hover:bg-slate-50/80'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <CreatePurchaseModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreatePurchase}
      />

      <EditPurchaseModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedPurchase(null);
        }}
        onUpdate={handleUpdatePurchase}
        purchase={selectedPurchase}
      />

      <ViewPurchaseModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedPurchase(null);
        }}
        purchase={selectedPurchase}
        onEdit={() => {
          setShowViewModal(false);
          setShowEditModal(true);
        }}
        onDelete={() => {
          setShowViewModal(false);
          setShowDeleteModal(true);
        }}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedPurchase(null);
        }}
        onConfirm={handleDeletePurchase}
        title="Delete Purchase"
        message={`Are you sure you want to delete purchase "${selectedPurchase?.invoiceNumber}"? This action cannot be undone.`}
      />
    </div>
  );
};

export default Purchases;
